/**
 * InfluenceMapConfig — parameters for the foliage interaction map.
 *
 * The influence map is a 2D world-space texture covering the area where
 * grass / foliage exists. Moving objects (players, vehicles, NPCs) deposit
 * "bend" data into the map every frame. Foliage shaders sample the map at
 * each blade's world-space XZ position and apply a corresponding bend
 * displacement.
 *
 * The encoding is two-channel signed: R = bendX, G = bendZ. The magnitude
 * is implicitly `length(RG)` and the direction is `RG / magnitude`. This
 * lets multiple agents add their contributions linearly via vector sum
 * (no need for separate magnitude + direction channels).
 *
 * v1 ships:
 *   - 2D RG16F texture, world-space XZ projected
 *   - Per-frame agent deposit + exponential time decay
 *   - TSL material modifier `applyInfluenceToFoliage()` for grass/blade meshes
 *   - <x-foliage-interaction> element + foliage.* comp keys
 *
 * v2 ships:
 *   - GPU compute deposit (avoid CPU-side per-cell writes)
 *   - Persistent "wear" channel (paths flatten over time)
 *   - Wind-influence integration (gust eddies in the map)
 */

export interface InfluenceMapQualityPreset {
  /** Texture resolution (cells per axis — square map). */
  resolution: number;
  /** Maximum agents allowed to deposit per frame (CPU budget gate). */
  maxAgents: number;
}

export const FOLIAGE_PRESETS: Record<'low' | 'medium' | 'high' | 'ultra', InfluenceMapQualityPreset> = {
  low:    { resolution: 128, maxAgents:  32 },
  medium: { resolution: 256, maxAgents:  64 },
  high:   { resolution: 512, maxAgents: 128 },
  ultra:  { resolution: 1024, maxAgents: 256 },
};

export interface InfluenceMapConfig {
  /** Quality preset (sets resolution + agent cap). */
  quality: InfluenceMapQualityPreset;
  /**
   * World-space XZ bounds the map covers. Outside this rectangle, the
   * shader samples the clamped border (zero bend). Typical: cover the
   * playable area or stream as a follow-the-camera region.
   */
  bounds: {
    minX: number; minZ: number;
    maxX: number; maxZ: number;
  };
  /**
   * Decay rate per second. The map is multiplied by `exp(-decay·dt)` each
   * frame, so trampled grass returns to upright over time.
   *   - decay = 1: ~63% recovery in 1 s, ~95% in 3 s. Natural for grass.
   *   - decay = 0.2: very slow recovery (long-lasting paths).
   *   - decay = 5: instant — only currently-stepping agents affect blades.
   */
  decay: number;
  /**
   * Maximum bend magnitude allowed at any cell. Caps influence so a
   * stationary player doesn't "drill" through the floor of grass.
   */
  maxBend: number;
  /**
   * If true, the map texture is auto-uploaded each frame by the system.
   * Set false if you want to control upload timing manually (e.g.,
   * skip frames for stationary players).
   */
  autoUpload: boolean;
}

export function createInfluenceMapConfig(overrides: Partial<InfluenceMapConfig> = {}): InfluenceMapConfig {
  const quality = overrides.quality ?? FOLIAGE_PRESETS.medium;
  return {
    quality,
    bounds: overrides.bounds ?? { minX: -32, minZ: -32, maxX: 32, maxZ: 32 },
    decay: overrides.decay ?? 1.0,
    maxBend: overrides.maxBend ?? 1.0,
    autoUpload: overrides.autoUpload ?? true,
  };
}

/**
 * A single agent contributing to the influence map for one frame.
 * Agents push grass blades AWAY from their position with strength that
 * falls off with distance (Gaussian).
 */
export interface InfluenceAgent {
  /** Agent center in world-space XZ. Y is ignored — the map is 2D. */
  x: number;
  z: number;
  /** Effective radius (world units) — Gaussian falloff sigma. */
  radius: number;
  /**
   * Strength multiplier. Positive = push blades outward (footprint).
   * Negative = pull blades inward (rare — wind eddy or magnetic trick).
   */
  strength: number;
  /**
   * Optional explicit push direction (unit vector). If omitted, the bend
   * radiates outward from the agent center — i.e., the natural footprint
   * of a stationary character. Provide this for vehicles + projectiles
   * where the bend should align with motion direction rather than
   * radiate from a point.
   */
  directionX?: number;
  directionZ?: number;
}
